/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { SxProps } from "@mui/material";
import { IFilterSelectBox } from "../../Shared/Filter/types";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../store/hooks";
import { setGeoRegion, setIsDeviceDetailPage, setSelectedEventSeverity, setSelectedEventTime, setSelectedLabels } from "../../../store/filters/events/EventFilterReducer";
import { setRegionFilterIds } from "../../../store/filters/Nodes/reducer";
import { eventFilterGeoRegionSelector, eventFilterSelector } from "../../../store/filters/events/EventFilterSelectors";
import { devicesSelector } from "../../../store/filters/Nodes/selectors";
import { ALARMSEVERITYLIST, SEVERITYLIST } from "../../../globals/globals";
import { useLocation } from "react-router-dom";
import { selectedTypeSelector } from "../../../store/api/devices/selectors";
import { useEffect, useMemo } from "react";
import { AppState } from "../../../store";
import { IGeoRegion } from "../../../store/api/admin/types";
import { useRegionDisplay } from "../../../pages/Administrations/AccessControls/Regions/hooks";
import { selectedGeoRegionIdsSelector } from "../../../store/api/admin/selectors";
import { useLabelsDisplay } from "../NodesFilter/hooks";


export const useEventsFilter = () => {
  const deviceLabelList = useLabelsDisplay();
  const eventFilterData = useSelector(eventFilterSelector);
  const deviceFilterData = useSelector(devicesSelector);
  const selectedTypeOnDeviceDetail = useSelector(selectedTypeSelector);
  const selectedGeoRegion = useSelector<AppState, IGeoRegion>(eventFilterGeoRegionSelector);
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const alarmPath = location?.pathname?.split("/")[2];
  const isDeviceDetailPage = location?.pathname?.split("/")[1]?.toLowerCase() === "device";
  const isDashboardPath = useMemo(() => location.pathname === "/", [location.pathname]);

  const { geoRegions } = useRegionDisplay();
  const selectedGeoRegionIds = useSelector(selectedGeoRegionIdsSelector);

  const regionOptions = useMemo(() => {
    if (!geoRegions || geoRegions.length === 0) return [];

    const baseRegions = selectedGeoRegionIds && selectedGeoRegionIds.length
      ? geoRegions.filter((region: IGeoRegion) => selectedGeoRegionIds.includes(region.objectKey))
      : geoRegions;

    return baseRegions
      .map((region: IGeoRegion) => ({ value: region.objectKey, text: region.name }))
      .sort((a, b) => (a.text || "").localeCompare(b.text || ""));
  }, [geoRegions, selectedGeoRegionIds]);

  useEffect(() => {
    if (isDeviceDetailPage) {
      dispatch(setIsDeviceDetailPage(true))
    }
    else dispatch(setIsDeviceDetailPage(false))
  }, [dispatch, isDeviceDetailPage])

  const geoRegionFilter: IFilterSelectBox[] = [
    {
      id: "geoRegion",
      label: "REGION",
      value: selectedGeoRegion?.objectKey ?? "",
      setValue: setGeoRegion,
      values: regionOptions,
      width: "140px",
    }
  ];

  const nodeRegionFilter: IFilterSelectBox[] = [
    {
      id: "regionFilter",
      label: "REGION",
      value: deviceFilterData.regionFilterIds || [],
      setValue: setRegionFilterIds,
      values: regionOptions,
      width: "140px",
      multiple: true,
    }
  ];

  const eventFilters: IFilterSelectBox[] = [
    ...(isDeviceDetailPage ? [] : nodeRegionFilter),
    {
      id: "severity",
      label: "SEVERITY",
      value: eventFilterData.selectedEventSeverity || "",
      setValue: setSelectedEventSeverity,
      values: alarmPath === "alarms" ? ALARMSEVERITYLIST : selectedTypeOnDeviceDetail === "alarms" ? ALARMSEVERITYLIST : SEVERITYLIST,
      width: "120px",
    },
    {
      id: "time",
      label: "TIME",
      value: eventFilterData.selectedEventTime || "",
      setValue: setSelectedEventTime,
      values: [
        { value: 10 * 60 * 1000, text: "Last 10 minutes" },
        { value: 24 * 60 * 60 * 1000, text: "Last 24 hours" },
        { value: 7 * 24 * 60 * 60 * 1000, text: "Past 7 days" },
        { value: "Custom", text: "Custom" },
      ],
      width: "120px",
    },
    {
      id: "deviceLabel",
      label: "LABELS",
      value: eventFilterData.selectedLabels || [],
      setValue: setSelectedLabels,
      values: (deviceLabelList?.data?.sort() || ""),
      width: "110px",
      multiple: true,
    },
    /*
    {
      id: "host",
      label: "HOST",
      value: eventFilterData.selectedHostName || "",
      setValue: setSelectedHostName,
      values: ["host1", "host2", "host3"],
      width: "100px",
    },
    {
      id: "managedObject",
      label: "MANAGED OBJECT",
      value: eventFilterData.selectedManagedObject || "",
      setValue: setSelectedManagedObject,
      values: ["ServicesNode.1025", "RadioUnit.3", "RadioUnit.3.Radio.1"],
      width: "200px",
    },
    {
      id: "eventType",
      label: "TYPE",
      value: eventFilterData.selectedEventType || "",
      setValue: setSelectedEventType,
      values: ["tableCleanup", "Syslog Event"],
      width: "130px",
    },
*/
  ];

  const formControlSx: SxProps = { minWidth: 100, marginRight: "10px" };
  const searchTooltip = "Search by 'Host', 'Type', 'Managed Object', 'Description' or 'Label Name', fields for quick access.";
  return {
    dispatch,
    eventFilters,
    formControlSx,
    searchTooltip,
  };
};

